/**
 * @file Registration.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useState } from "react";
import Steps from "../../components/onboarding/Steps";
import { Grid } from "@mui/material";
import TextInput from "../../components/forms/TextInput";
import SelectInput from "../../components/forms/SelectInput";
import AccountForm from "./AccountForm";
import DgButton from "../../components/DgButton";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { useApplicationContext } from "../../contexts/ApplicationContext";
import Request from "../../utils/fetchUtils";
import Swal from "sweetalert2";

const newAccountInitial = {
  name: "",
  email: "",
  repCode: "ADF", //
  options: true, //  ??
  applicationType: {
    optionAgreementForm: {
      riskStandardOptions: "2024-08-21",
      optionApproval: 2,
      uncoveredOptionWriters: "2024-08-21",
    },
    type: "",
    newAccountForm: {
      tradeAuthorization: {
        isTradeAuthorization: "YES",
      },
      accountType: "MARGIN",
      customerType: "INDIVIDUAL",
      suitabilityProfile: {
        timeHorizon: "AVERAGE",
        liquidityNeeds: "NOT_IMPORTANT",
      },
      investmentProfile: {
        totalNetWorthUSD: {
          min: 200001,
          max: 500000,
        },
        liquidNetWorthUSD: {
          min: 0,
          max: 50000,
        },
        annualIncomeUSD: {
          min: 100001,
          max: 200000,
        },
        stockExperience: 1,
        optionExperience: 1,
        investmentObjective: "GROWTH",
        riskTolerance: "MEDIUM",
        investmentExperience: "LIMITED",
      },
      applicants: [
        {
          identity: {
            name: {
              givenName: "",
              familyName: "",
            },
            dateOfBirth: "1988-05-27", // FORMAT
            citizenshipCountry: "USA",
            socialSecurityNumber: "629-01-2327", // FORMAT
          },
          contact: {
            homeAddress: {
              streetAddress: ["39642 Rickard Rd"],
              country: "USA",
              city: "Lovettsville",
              state: "VA",
              postalCode: "20180",
            },
            phoneNumbers: [
              {
                phoneNumber: "703-554-7112", // FORMAT
                phoneNumberType: "HOME",
              },
            ],
            emailAddresses: ["michael@aureum.ai"],
          },
          disclosures: {
            isPoliticallyExposed: "NO",
            isControlPerson: "NO",
            isAffiliatedExchangeOrFINRA: "NO",
          },
          employment: {
            employmentStatus: "SELF_EMPLOYED",
            employer: "Aureum AI",
            businessAddress: {
              streetAddress: ["39642 Rickard Rd"],
              city: "Lovettsville",
              state: "VA",
              postalCode: "20180",
              country: "USA",
            },
          },
          maritalStatus: "MARRIED",
        },
      ],
    },
  },
};

const TYPES = Object.freeze({
  rollover_ira: "ROLLOVER_ACCOUNT",
  roth_ira: "ROTH_IRA",
  traditional_ira: "TRADITIONAL_IRA",
});

const CUSTOMER_TYPES = Object.freeze({
  rollover_ira: "IRA",
  roth_ira: "IRA",
  traditional_ira: "IRA",
  joint_margin_survivor: "JOINT",
  joint_cash_survivor: "JOINT",
  individual_margin: "INDIVIDUAL",
  individual_cash: "INDIVIDUAL",
});

function Registration() {
  const navigate = useNavigate();
  const { applicationType, setApplicationType } = useApplicationContext();

  const [accountForm, setAccountForm] = useState({});
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;
  const sessionFullName = localStorage.getItem("fullName");
  const sessionEmail = localStorage.getItem("email");

  const emptyApplication = {
    name: sessionFullName,
    email: sessionEmail,
    //email: 'george@whitehouse.gov',
    repCode: "ADF",
    options: true,
    applicationType: {
      optionAgreementForm: {
        riskStandardOptions: formattedDate,
        optionApproval: 2,
        uncoveredOptionWriters: formattedDate,
      },
      type: "",
    },
  };

  const [applicationData, setApplicationData] = useState(emptyApplication);

  const [isUsaCitizen, setIsUsaCitizen] = useState("");
  const [type, setType] = useState("");
  const [accountType, setAccountType] = useState("");

  const handleCitizenship = (value) => {
    const booleanValue = type === "individual_margin" ? "" : value === "true";
    setIsUsaCitizen(booleanValue);
  };

  const handleChange = (field, value) => {
    setApplicationData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleTypeChange = (newType) => {
    const accountType = newType.includes("cash")
      ? "CASH"
      : newType.includes("margin")
      ? "MARGIN"
      : "IRA";
    setAccountType(accountType);
    setType(newType);
    setApplicationData((prevData) => ({
      ...prevData,
      applicationType: {
        ...prevData.applicationType,
        type: newType,
      },
    }));

    // context
    setApplicationType(newType);

    if (newType == "individual_margin" && !isUsaCitizen) {
      setIsUsaCitizen("");
    }
  };

  const handleAccountData = (data) => {
    setAccountForm(data);
    if (
      applicationType === "rollover_ira" ||
      applicationType === "roth_ira" ||
      applicationType === "traditional_ira"
    ) {
      handleNestedChange("applicationType", "newAccountIraForm", data);
      handleNestedChange("applicationType", "newAccountForm", undefined);

      setApplicationData((prevState) => ({
        ...prevState,

        applicationType: {
          ...prevState.applicationType,
          // optionAgreementForm: undefined,
          newAccountIraForm: {
            ...(prevState.applicationType?.newAccountIraForm || {}),
            customerType: CUSTOMER_TYPES[applicationType] || "",
            accountType: "CASH",
            retirementAccountBeneficiariesForm: undefined,
            tradeAuthorization: {
              ...(prevState.applicationType?.newAccountIraForm
                ?.tradeAuthorization || {}),
              agentName: "Tradier",
            },
            suitabilityProfile: {
              ...(prevState.applicationType?.newAccountIraForm
                ?.suitabilityProfile || {}),
              liquidityNeeds: undefined,
            },
            investmentProfile: {
              ...(prevState.applicationType?.newAccountIraForm
                ?.investmentProfile || {}),
            },
            applicants: [
              {
                ...(prevState.applicationType?.newAccountIraForm
                  ?.applicants[0] || {}),
                identity: {
                  ...(prevState.applicationType?.newAccountIraForm
                    ?.applicants[0]?.identity || {}),
                  numDependents: undefined,
                },
                numDependents: data.applicants[0].identity.numDependents,
              },
            ],
          },
          retirementAccountBeneficiariesForm: {
            ...data.retirementAccountBeneficiariesForm,
          },
          iraSepRolloverAgreementForm: {
            typeIRA: `${TYPES[applicationType]}`,
          },
        },
      }));
    }

    if (
      applicationType === "individual_cash" ||
      applicationType === "individual_margin"
    ) {
      handleNestedChange("applicationType", "newAccountForm", data);
      handleNestedChange("applicationType", "newAccountIraForm", undefined);

      setApplicationData((prevState) => ({
        ...prevState,
        applicationType: {
          ...prevState.applicationType,
          newAccountForm: {
            ...(prevState.applicationType?.newAccountForm || {}),
            customerType: CUSTOMER_TYPES[applicationType] || "",
          },
        },
      }));
    }

    if (
      applicationType === "joint_cash_survivor" ||
      applicationType === "joint_margin_survivor"
    ) {
      handleNestedChange("applicationType", "newAccountForm", data);
      handleNestedChange("applicationType", "newAccountIraForm", undefined);

      setApplicationData((prevState) => ({
        ...prevState,
        applicationType: {
          ...prevState.applicationType,
          jointRightsOfSurvivorshipForm: {},
          newAccountForm: {
            ...(prevState.applicationType?.newAccountForm || {}),
            customerType: CUSTOMER_TYPES[applicationType] || "",
            retirementAccountBeneficiariesForm: undefined,
          },
        },
      }));
    }
  };

  const handleNestedChange = (field, subField, value) => {
    setApplicationData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const getApplicationTypes = () => [
    {
      text: "Individual (Cash)",
      value: "individual_cash",
    },
    {
      text: "Individual (Margin)",
      value: "individual_margin",
      disabled: !isUsaCitizen,
    },
    {
      text: "Joint Survivor (Cash)",
      value: "joint_cash_survivor",
    },
    {
      text: "Joint Survivor (Margin)",
      value: "joint_margin_survivor",
      disabled: !isUsaCitizen,
    },
    {
      text: "Rollover IRA",
      value: "rollover_ira",
      disabled: !isUsaCitizen,
    },
    {
      text: "Traditional IRA",
      value: "traditional_ira",
      disabled: !isUsaCitizen,
    },
    {
      text: "Roth IRA",
      value: "roth_ira",
      disabled: !isUsaCitizen,
    },
  ];

  const sendApplication = async () => {
    setLoading(true);
    const res = await Request(
      `/trading/application`,
      { ...applicationData },
      "POST"
    );
    setLoading(false);
    if (res.ok) {
      Swal.fire(
        "Application successfully sent",
        "Lets go to the next step",
        "success"
      ).then(() => {
        navigate(`/onboarding/agreements/${res.data.id}`);
      });
    } else {
      const errorsString =
        res.data?.body?.error?.errors?.reduce(
          (cErr, accErr) => accErr + " \b" + cErr
        ) || "Please check out all required fields are filled";
      Swal.fire(res.message, errorsString, "error");
    }
  };

  return (
    <div className="your-profile">
      <Steps>
        <p className="subtitle">Applicant Information</p>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Full name",
                  name: "name",
                  value: applicationData.name,
                  action: (e) => handleChange("name", e.target.value),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Email",
                  name: "email",
                  value: applicationData.email,
                  action: (e) => handleChange("email", e.target.value),
                  tooltipBool: false,
                  disabled: true,
                }}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Are you a citizen of the United States?",
                  name: "isUsaCitizen",
                  value: isUsaCitizen,
                  action: (e) => handleCitizenship(e.target.value),
                  tooltipBool: false,
                }}
                data={[
                  {
                    text: "YES",
                    value: "true",
                  },
                  {
                    text: "NO",
                    value: "false",
                  },
                ]}
              ></SelectInput>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Application Type",
                  name: "type",
                  value: type,
                  action: (e) => handleTypeChange(e.target.value),
                  tooltipBool: false,
                  disabled: isUsaCitizen === "",
                }}
                data={getApplicationTypes()}
              />
            </div>
          </Grid>
        </Grid>
        <AccountForm
          actType={accountType}
          sendAccountData={handleAccountData}
        />
        <div className="button-container">
          <DgButton
            category={"primary"}
            size={"large"}
            text={"Next"}
            action={sendApplication}
            loading={loading}
          >
            <ArrowForwardIos className="icon next" />
          </DgButton>
        </div>
      </Steps>
    </div>
  );
}

export default Registration;
